import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { usePortfolioAuth } from '@/context/PortfolioAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SelectRole() {
  const navigate = useNavigate();
  const { login } = usePortfolioAuth();
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [selectedRole, setSelectedRole] = useState('owner');

  useEffect(() => {
    // Get user info from session storage
    const storedUserInfo = sessionStorage.getItem('google_user_info');
    const code = sessionStorage.getItem('google_auth_code');

    if (!storedUserInfo || !code) {
      toast.error('Session expired. Please login again.');
      navigate('/portfolio/login');
      return;
    }

    setUserInfo(JSON.parse(storedUserInfo));
  }, [navigate]);

  const handleRoleSelection = async () => {
    try {
      setLoading(true);
      const code = sessionStorage.getItem('google_auth_code');
      const formData = new FormData();
      formData.append('code', code);
      formData.append('role', selectedRole);

      const response = await axios.post(`${API}/portfolio/auth/google/callback`, formData);

      if (response.data.status === 'success') {
        // Clear session storage
        sessionStorage.removeItem('google_user_info');
        sessionStorage.removeItem('google_auth_code');

        login(response.data.token, response.data.user);
        toast.success('Account created successfully!');
        navigate('/portfolio');
      }
    } catch (error) {
      console.error('Role selection error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create account');
      setLoading(false);
    }
  };

  if (!userInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1e40af] via-[#1e3a8a] to-[#1e3a8a] flex items-center justify-center">
        <div className="text-center text-white">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
          <p className="text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1e40af] via-[#1e3a8a] to-[#1e3a8a] flex items-center justify-center py-12 px-4">
      <Card className="max-w-md w-full shadow-2xl">
        <CardContent className="pt-6">
          <div className="text-center mb-6">
            {userInfo?.picture && (
              <img
                src={userInfo.picture}
                alt={userInfo.name}
                className="w-20 h-20 rounded-full mx-auto mb-4 border-4 border-[#1e3a8a]"
              />
            )}
            <h2 className="text-2xl font-bold text-[#1e3a8a] mb-2">Welcome, {userInfo?.name}!</h2>
            <p className="text-slate-600">Please select your role to continue</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="role" className="text-slate-700 font-medium">I am a...</Label>
              <select
                id="role"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full mt-2 px-4 py-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
              >
                <option value="owner">Property Owner</option>
                <option value="tenant">Tenant</option>
                <option value="admin">Administrator</option>
              </select>
            </div>

            <Button
              onClick={handleRoleSelection}
              disabled={loading}
              className="w-full bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white py-6 text-lg"
            >
              {loading ? 'Creating Account...' : 'Continue'}
            </Button>

            <Button
              onClick={() => navigate('/portfolio/login')}
              variant="outline"
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
